import * as React from 'react';
import { STATE } from 'app/constants';
import { containerFactory, DefaultContainerProps } from 'app/infrastructure/redux';
import { routeConstants } from 'app/routes/route-constants';
import * as navigate from 'app/area/shared/navigation';
import {
  CodetableAdministrationState, CodetableMetadata,
  CodeFieldIncludeInactiveKey, CodetableScreenName, CodeFieldWireKey
} from '../types-store';
import { CodeComplexValue } from '../../shared/codetable/types';
import { CodetableSelectorProps } from '../types';
import { codetableAdministrationTranslation } from '../constants';
import { selectors, getCodetableMetadata } from '../selectors';
import { actionCreators } from './actions';
import styles from './codetable-list.scss';
import { isDefinedIncludingZero } from 'app/infrastructure/data/number';
import { Breadcrumb } from 'ui/common/breadcrumb';
import { BreadcrumbValue } from 'ui/common/breadcrumb/breadcrumb-item';
import { CodetableItemLoader } from 'app/area/shared/codetable/item/codetable-item-loader';
import { deepEquals } from 'app/infrastructure/js';
import { buildBreadcrumbs, buildGoBackBreadcrumbs, getCodetableId } from 'app/area/shared/codetable/breadcrumbs';
import { codeFieldIncludeInactive } from 'app/area/codetable/common/codetable-options-handler/constants';
import { CodetableListRenderer } from 'app/area/codetable/list/list-renderer/list-renderer';
import { SupportedListScreens, supportedListScreens } from 'app/area/codetable/list/list-renderer/supported-screens';
import {
  codeDefaultFilters, codeFilters,
  codePredefinedFilters, codeQueryFilters
} from 'app/area/codetable/list/search-filters/code-filter-configuration';
import { SerializePayload } from 'app/area/search-filters/types';
import { serialize } from 'app/area/shared/codetable/serialize/serialize';
import { SearchButton } from 'app/area/codetable/list/search-button';
import { codeAccessConfiguration } from 'app/area/codetable/item/codetable-item-helpers';
import { unitOfMeasureTypeId } from 'app/area/codetable/item/constants';
import { codeSearchArea, LoadCodeItems, ShouldHideFiltersDrawerPayload } from 'app/area/codetable/list/types';
import isArray from 'lodash/isArray';
import { filterId } from 'app/area/search-filters/constants';
import { CodetableListScreen } from 'app/area/codetable/list/list-renderer/types';
import { replaceTemplateString } from 'app/infrastructure/platform';

export type CodetableListProps = DefaultContainerProps & CodetableAdministrationState &
  CodetableSelectorProps & typeof actionCreators;

export interface CodetableListState {
  breadcrumbs: CodeComplexValue<number>[];
}

class CodetableListPure extends React.PureComponent<CodetableListProps, CodetableListState> {
  public static statePath: string = STATE.administrationCodetable;

  public state: CodetableListState = {
    breadcrumbs: []
  };

  public componentDidMount() {
    this.loadCategoryOptions();
    this.loadNewState(true);
  }

  public componentDidUpdate(prevProps: CodetableListProps) {
    this.updateCategoryFromParam(prevProps);
    this.loadNewState(false, prevProps);
  }

  private loadNewState = (reload: boolean, prevProps?: CodetableListProps) => {
    const { metadata: { data }, list: { fetchingCodetableId, fetchingBreadcrumbsCodetableId }, invalidateCodeItems,
      updateCaseCategory, loadCodetableAdministrationList, updateIncludeInactive } = this.props;

    const codetableId = getCodetableId(this.props.breadcrumbs);

    const codetableMetadata = getCodetableMetadata(data, codetableId);
    if (!codetableMetadata) {
      navigate.replace(routeConstants.codetableAdministrationFor(this.props.breadcrumbs));
      return;
    }

    if (this.shouldBuildBreadcrumbs(reload, prevProps)) {
      const breadcrumbs = buildBreadcrumbs(this.props.list.options, this.props.breadcrumbs);
      this.setState({ breadcrumbs });
    }

    if (!!codetableId && fetchingCodetableId !== codetableId &&
      (!prevProps || prevProps.list?.fetchingCodetableId !== fetchingCodetableId)) {
      this.props.resetListState();
      !isDefinedIncludingZero(fetchingBreadcrumbsCodetableId) && loadCodetableAdministrationList();
      updateCaseCategory(undefined);
      updateIncludeInactive(CodeFieldIncludeInactiveKey.active);
      return;
    }

    if (reload) {
      invalidateCodeItems();
      this.loadCodeItems();
    }
  };

  private loadCodeItems = (loadCodeItemsPayload: LoadCodeItems = {}) => {
    const { category: newCategory, parent, payload } = loadCodeItemsPayload;
    const { list: { requestedSearchModel, category } = {},
      updateSearchModel, metadata: { data }, categoryId, translation } = this.props;
    const newSearchModel = payload ? serialize(payload) : undefined;

    const didSearchModelChanged = newSearchModel && !deepEquals(newSearchModel, requestedSearchModel);

    const codetableId = getCodetableId(this.props.breadcrumbs);
    const metadata = this.codetableMetadata();

    const customFilters = codeFilters(codetableId, translation, metadata);

    const shouldLoadCodeItems = !metadata.codetableCategory ||
      (!!metadata.codetableCategory && !!customFilters.find(filter => filter.id === filterId.category)) ||
      (!!metadata.codetableCategory && !customFilters.find(filter => filter.id === filterId.category) && !!(category || newCategory));

    const newCategoryId = newCategory ? newCategory.value :
      category ? category.value :
        isDefinedIncludingZero(categoryId) ? categoryId : undefined;

    const hasCategory = (newSearchModel && !!newSearchModel[filterId.category]) || (!newSearchModel && requestedSearchModel &&
      !!requestedSearchModel[filterId.category]);

    this.props.loadCodeItems({
      codetableId,
      parentId: isDefinedIncludingZero(parent?.id) ? parent.id : undefined,
      categoryId: newCategoryId,
      searchModel: didSearchModelChanged && newSearchModel ? newSearchModel : requestedSearchModel,
      startIndex: isDefinedIncludingZero(parent?.startIndex) ? parent.startIndex : undefined,
      maxResults: undefined,
      includeInactiveCodes: this.getCurrentIncludeInactive(),
      ignoreCategoryFilters: this.shouldIgnoreCategoryFilters(data, hasCategory),
      shouldLoadCodeItems,
      isMetadataSequence: metadata?.features?.sequence,
      limitFieldsTo: this.getScreenToRender(codetableId)?.screenProps?.limitFieldsTo
    });

    didSearchModelChanged && updateSearchModel(newSearchModel);
  };

  private updateSearchModel = (payload: SerializePayload) => {
    const newSearchModel = payload ? serialize(payload) : undefined;
    !deepEquals(newSearchModel, this.props.list?.requestedSearchModel) && this.loadCodeItems({ payload });
  };

  private getCurrentIncludeInactive = () => codeFieldIncludeInactive[this.props.list.includeInactive];

  private loadCategoryOptions = () => {
    const { loadCodetableOptions,
      metadata: { data: metadataValue },
      router: { location: { query: { categoryId } = {} } = {} } = {} }
      = this.props;
    if (categoryId && !!metadataValue && !!metadataValue.codetableCategory && isDefinedIncludingZero(metadataValue.codetableCategory.id)) {
      loadCodetableOptions(CodeFieldWireKey.category, metadataValue.codetableCategory.id, {});
    }
  };

  private updateCategoryFromParam = (prevProps: CodetableListProps) => {
    const { item: { codetableOptions: { category: { codes: currCategoryCodes = undefined } = {} } = {} } = {},
      router: { location: { query: { categoryId: queryCategoryId } = {} } = {} },
      list: { category: { value: currCategoryValue } = {} } = {} } = this.props;

    const { item: { codetableOptions: { category: { codes: prevCategoryCodes = undefined } = {} } = {} } = {} } = prevProps;

    if (!prevCategoryCodes && !!currCategoryCodes && (currCategoryValue === undefined && queryCategoryId)) {
      const value = currCategoryCodes.find(f => f.value === parseInt(queryCategoryId as string, 10));
      !!value && this.updateCategory(value);
    }
  };

  private shouldBuildBreadcrumbs = (reload: boolean, prevProps: CodetableListProps) => this.props &&
    this.props.list &&
    this.props.list.options && (!prevProps || prevProps && prevProps.list && !deepEquals(prevProps.list.options, this.props.list.options))
    && this.props.list.options && this.props.list.options.length > 0 && this.props.breadcrumbs && this.props.breadcrumbs.length > 0;

  private updateCategory = (category: CodeComplexValue<number>) => {
    category && this.loadCodeItems({ category });
    this.props.updateCaseCategory(category);
  };

  private loadFromParent = (parentId: number, startIndex: number) => this.loadCodeItems({ parent: { id: parentId, startIndex } });

  private reloadCodeItems = () => this.loadNewState(true);

  private categoryMetadata = (codetableMetadata: CodetableMetadata) => codetableMetadata && codetableMetadata.features &&
    codetableMetadata.codetableCategory && isDefinedIncludingZero(codetableMetadata.codetableCategory.id);

  private hasSelectedCategory = () => this.props.list?.category && isDefinedIncludingZero(this.props.list.category.value);

  private isFetching = () => {
    const { list: { codeListBranchState }, access: { fetchingCodetables } = {} } = this.props;
    return !!fetchingCodetables || (codeListBranchState && codeListBranchState[0] && codeListBranchState[0].fetching);
  };

  private goBackToCodeSelector = (item: BreadcrumbValue) =>
    navigate.toCodeSelectView(buildGoBackBreadcrumbs(item, this.props.breadcrumbs));

  private goBackToHomeCodeSelector = () => navigate.to(routeConstants.selectCodetableAdministration);

  private codetableMetadata = () => getCodetableMetadata(this.props.metadata.data, getCodetableId(this.props.breadcrumbs));

  private getCodeAccessConfiguration = () => {
    const { access: { data: accessData } = {}, list: { itemAccess } = {} } = this.props;

    return codeAccessConfiguration(getCodetableId(this.props.breadcrumbs), accessData, itemAccess);
  };

  private isHierarchy = () => {
    const codetableMetadata = this.codetableMetadata();
    return !!codetableMetadata && !!codetableMetadata.features && codetableMetadata.features.hierarchy;
  };

  private isHierarchyMode = () => this.isHierarchy() && !this.props.list?.requestedSearchModel?.query &&
    !this.props.list?.requestedSearchModel?.code?.text;

  private hasCategoryMetadata = () => this.categoryMetadata(this.codetableMetadata());

  private rootBranch = () => this.props.list?.codeListBranch?.find(b => b.id === undefined);

  private shouldRenderSpinLoader = () => {
    const { list: { codeListBranch } = {}, access: { fetchingCodetables: accessFetching } = {} } = this.props;

    return !!accessFetching || !codeListBranch || codeListBranch.length === 0;
  };

  private shouldIgnoreCategoryFilters = (metadata: CodetableMetadata, hasCategory: boolean) => {
    return !!metadata && !!metadata.codetable && [
      !hasCategory && { id: SupportedListScreens.Reference, formName: CodetableScreenName.Reference },
      { id: unitOfMeasureTypeId }
    ].some(c => c.id === metadata.codetable.id && c.formName === metadata.formName);
  };

  private shouldHideFiltersDrawer = (payload: ShouldHideFiltersDrawerPayload) => {
    const { customFilters, queryFilters, predefinedFilters } = payload;
    return !(this.hasAnyFilterDefined(customFilters) ||
      this.hasAnyFilterDefined(queryFilters) || this.hasAnyFilterDefined(predefinedFilters?.values));
  };

  private hasAnyFilterDefined = (array: object[]) => !!array && isArray(array) && array.length > 0;

  private getScreenToRender = (codetableId: number): CodetableListScreen =>
    supportedListScreens?.length > 0
    && isDefinedIncludingZero(codetableId)
    && supportedListScreens.find(screen => screen.id === codetableId);

  private updateCodeParent = (codeId: number, parentId: number, description: string) => {
    this.props.updateCodeParent(
      getCodetableId(this.props.breadcrumbs),
      codeId,
      parentId,
      replaceTemplateString(this.props.translation.update_code_parent_info, description, codeId),
      this.loadNewState);
  };

  public render() {

    const { list: { category, codeListBranch, fetchingBreadcrumbsCodetableId, fetchingCodetableId } = {},
      loadCodetableOptions, translation } = this.props;
    const { breadcrumbs } = this.state;

    const hasSelectedCategory = this.hasSelectedCategory();
    const rootBranch = this.rootBranch();
    const codetableId = getCodetableId(this.props.breadcrumbs);

    const shouldRenderList = !!codeListBranch && codeListBranch.length > 0 && rootBranch
      && rootBranch.numberOfResults > 0 && (this.hasCategoryMetadata() ? hasSelectedCategory : true)
      && (this.props.access && !this.props.access.fetchingCodetables);

    const shouldRenderBreadcrumbs = !fetchingBreadcrumbsCodetableId && breadcrumbs && breadcrumbs.length > 0;

    const metadata = this.codetableMetadata();

    const queryFilters = codeQueryFilters(translation);
    const predefinedFilters = codePredefinedFilters(translation, metadata);
    const customFilters = codeFilters(codetableId, translation, metadata);
    const defaultFilterValues = codeDefaultFilters(translation, metadata);

    const hideDrawer = this.shouldHideFiltersDrawer({ queryFilters, predefinedFilters, customFilters });

    const codeAccess = this.getCodeAccessConfiguration();

    const rightToolbar = <SearchButton
      breadcrumbs={this.props.breadcrumbs}
      canCreate={!!codeAccess?.canCreate}
      codetableId={codetableId}
      fetchingCodetableId={fetchingCodetableId}
      translation={translation}
    />;

    const topToolbar = <>
      <div className={`${styles.breadcrumbRow} row`}>
        {fetchingBreadcrumbsCodetableId ? <CodetableItemLoader /> : null}
        {shouldRenderBreadcrumbs
          ? <Breadcrumb
            items={breadcrumbs || []}
            onClickItem={this.goBackToCodeSelector}
            home={true}
            homeLabel={translation.codetable_administration}
            onClickBreadcrumbHome={this.goBackToHomeCodeSelector} />
          : <div className={styles.breadcrumbPlaceholder} />}
      </div>
    </>;

    return <CodetableListRenderer
      {...this.props}
      {...this.props.list}
      codeAccessConfiguration={codeAccess}
      isFetching={this.isFetching()}
      screenToRender={this.getScreenToRender(codetableId)}
      isHierarchy={this.isHierarchy()}
      codetableId={getCodetableId(this.props.breadcrumbs)}
      category={category}
      metadata={metadata}
      isHierarchyMode={this.isHierarchyMode()}
      shouldRenderSpinLoader={this.shouldRenderSpinLoader()}
      shouldRenderList={shouldRenderList}
      updateCategory={this.updateCategory}
      updateCodeParent={this.updateCodeParent}
      hasCategoryMetadata={this.hasCategoryMetadata()}
      hasSelectedCategory={hasSelectedCategory}
      rootBranch={rootBranch}
      loadCodetableOptions={loadCodetableOptions}
      loadFromParent={this.loadFromParent}
      reloadCodeItems={this.reloadCodeItems}
      hideDrawer={hideDrawer}
      topToolbar={topToolbar}
      rightToolbar={rightToolbar}
      filtersConfig={{ area: codeSearchArea, ...(isDefinedIncludingZero(codetableId) && { customConfig: { codetableId } }) }}
      queryFilters={queryFilters}
      predefinedFilters={predefinedFilters}
      customFilters={customFilters}
      defaultFilterValues={defaultFilterValues}
      updateSearchModel={this.updateSearchModel}
      loadOptions={this.props.loadCodetableOptions}
      options={this.props.list.codetableOptions} />;
  }
}

const container = containerFactory(CodetableListPure, actionCreators, selectors, codetableAdministrationTranslation);

export { container as CodetableList, CodetableListPure };
